import React from 'react';
import { FormattedDate } from 'react-intl';
import { Grid, Tooltip } from '@mui/material';

import {
	ButtonLink,
	ButtonLinkMenu,
	Icon,
	ImageAvatar,
	SquareChip,
	TagsArray,
	Text,
	useSettings,
	Utils
} from 'app/main/components';
import { GridListItem, type ItemLayoutProps } from 'app/main/components/GridList';
import buttons from './CommercialCampaignsRowsButtons';

export default function CommercialCampaignsRowsListItem(props: ItemLayoutProps) {
	const { t } = useSettings();

	const avatar = (
		<ImageAvatar
			dimensions={44}
			module={props.model.cmrw_table}
			uid={props.model.peop_uid || props.model.comp_uid}
			avatar={props.model.peop_avatar || props.model.comp_avatar}
			initials={props.model.peop_title_initials || props.model.comp_title_initials}
			labelBadge={props.model.core_title_initials}
			labelBadgeTooltip={props.model.core_title}
			badgeColor={props.model.core_color}
		/>
	);

	return (
		<GridListItem avatar={avatar} buttons={buttons.view?.itemButtons}>
			<Grid container justifyContent="space-between" spacing={1}>
				<Grid size={props.width >= 960 ? 4 : props.width >= 600 ? 6 : 12}>
					<Grid container>
						<Grid size={12} className="inline-flex items-center">
							{/* WARNING */}
							{props.model.cmrw_table === 'companies'
								? props.model.comp_warning_desc && (
										<Tooltip title={props.model.comp_warning_desc} arrow>
											<Icon className="mr-2" icon="alert-circle-outline" color="warning.main" />
										</Tooltip>
									)
								: props.model.peop_warning_desc && (
										<Tooltip title={props.model.peop_warning_desc} arrow>
											<Icon className="mr-2" icon="alert-circle-outline" color="warning.main" />
										</Tooltip>
									)}
							{/* BLOCKED */}
							{props.model.cmrw_table === 'companies'
								? props.model.comp_blocked_desc && (
										<Tooltip title={props.model.comp_blocked_desc} arrow>
											<Icon className="mr-2" icon="block" color="error.main" />
										</Tooltip>
									)
								: props.model.peop_blocked_desc && (
										<Tooltip title={props.model.peop_blocked_desc} arrow>
											<Icon className="mr-2" icon="block" color="error.main" />
										</Tooltip>
									)}
							{/* LEAD */}
							{props.model.cmrw_table === 'companies' && props.model.lead_comp_title && (
								<Tooltip title={props.model.lead_comp_title} arrow>
									<Icon className="mr-2" pack="ic" icon="round-person-pin" />
								</Tooltip>
							)}
							{/* TITLE */}
							<Text
								className="font-600"
								variant="subtitle2"
								to={
									props.model.cmrw_table === 'companies'
										? `/companies/details/${props.model.comp_uid}`
										: `/people/details/${props.model.peop_uid}`
								}
							>
								{props.model.peop_title || props.model.comp_title}
							</Text>
						</Grid>
						{props.model.cmrw_table === 'companies' && (
							<>
								<Grid size={12}>
									{/* ADDRESS */}
									<Text className="font-400" variant="subtitle2" color="text.secondary">
										{props.model.comp_address || ''}
									</Text>
								</Grid>
								<Grid size={12}>
									<Text className="font-400" variant="subtitle2" color="text.secondary">
										{Utils.formatAddress2({
											t,
											address: {
												zip: props.model.comp_zip,
												city: props.model.comp_city,
												district: props.model.dist_iso || props.model.regi_iso,
												country: props.model.ctry_title
											}
										})}
									</Text>
								</Grid>
							</>
						)}
						{props.model.cmrw_table === 'people' && (
							<Grid size={12}>
								{/* COMPANY */}
								<ButtonLink
									href={`/companies/details/${props.model.comp_uid}`}
									icon="domain"
									fallback={t('VIEW_ITEM_NO_COMPANY')}
								>
									{props.model.comp_title}
								</ButtonLink>
							</Grid>
						)}
					</Grid>
				</Grid>
				{props.width >= 960 && (
					<Grid size={4}>
						<Grid container>
							<Grid size={12}>
								{/* MAILS */}
								<ButtonLinkMenu
									module="mails"
									list={props.model.cmrw_mails}
									table={props.model.cmrw_table}
									tableIcons={{
										companies: 'email',
										people: props.model.cmrw_table === 'companies' ? 'card-account-mail' : 'email'
									}}
									href={item => `mailto:${item.mail_title}`}
									fallback={t('VIEW_ITEM_NO_MAIL')}
								/>
							</Grid>
							<Grid size={12}>
								{/* PHONES */}
								<ButtonLinkMenu
									module="phones"
									list={props.model.cmrw_phones}
									table={props.model.cmrw_table}
									tableIcons={{
										companies: 'phone',
										people: props.model.cmrw_table === 'companies' ? 'contact-phone' : 'phone'
									}}
									href={item => `tel:${item.phon_title}`}
									fallback={t('VIEW_ITEM_NO_PHONE')}
								/>
							</Grid>
						</Grid>
					</Grid>
				)}
				<Grid size={props.width >= 960 ? 4 : props.width >= 600 ? 6 : 12}>
					<Grid container alignItems="center">
						<Grid>
							{/* DATE ADDED */}
							<SquareChip
								label={
									<FormattedDate
										value={Utils.parseDate(props.model.cmrw_d_created)?.toDate()}
										day="2-digit"
										month="2-digit"
										year="numeric"
										hour="numeric"
										minute="numeric"
									/>
								}
								icon={props.model.cmrw_import ? <Icon icon="new-box" /> : undefined}
							/>
						</Grid>
						{props.width >= 600 && (
							<>
								<Grid size={12}>
									{/* TYPE */}
									{props.model.coty_title && (
										<SquareChip
											label={props.model.coty_title}
											backgroundColor={props.model.coty_color}
										/>
									)}
									{/* SUBTYPES */}
									{props.model.comp_companies_subtypes?.map(subtype => (
										<SquareChip
											key={subtype.cosa_id}
											label={subtype.cosy_title}
											backgroundColor={subtype.cosy_color}
										/>
									))}
								</Grid>
								<Grid size={12}>
									{/* TAGS */}
									<TagsArray
										tags={props.model.peop_tags || props.model.comp_tags}
										changeFilter={props.changeFilter}
									/>
								</Grid>
							</>
						)}
					</Grid>
				</Grid>
			</Grid>
		</GridListItem>
	);
}
